import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, GraduationCap, Star } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { LOGO_URL, CATEGORY_LABELS, CATEGORY_IMAGES, CATEGORY_ICONS } from "@/lib/constants";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatPrice } from "@/lib/constants";
import * as LucideIcons from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { currency } = useCurrency();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  return (
    <>
      <AppHeader title="NEO ACADEMY" />

      <main className="page-scroll" data-testid="home-page">
        {/* Hero with prominent logo */}
        <section className="hero-glow px-6 pt-6 pb-8 text-center">
          <motion.img
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            src={LOGO_URL}
            alt="NEO ACADEMY logo"
            data-testid="hero-logo"
            className="mx-auto w-56 h-56 object-contain drop-shadow-md"
          />
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-1 text-[13px] tracking-[0.2em] text-[#1E5FD9] font-semibold"
          >
            APRENDE HOY · LIDERA MAÑANA
          </motion.p>
          <motion.h2
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mt-4 font-heading text-3xl font-extrabold text-[#0A2A66] leading-tight"
          >
            Tu academia,<br />en tu bolsillo.
          </motion.h2>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed max-w-sm mx-auto">
            Talleres, cursos, diplomados, licenciaturas, maestrías, doctorados y certificaciones para cada etapa de tu vida.
          </p>
          <Link
            to="/catalogo"
            data-testid="hero-explore-btn"
            className="gold-btn inline-flex items-center gap-2 mt-6 px-8 py-3.5 text-sm"
          >
            <Sparkles size={16} /> Explorar catálogo
          </Link>
        </section>

        {/* Trust strip */}
        <section className="px-4 py-4 grid grid-cols-3 gap-2 text-center">
          {[
            { n: "8", l: "categorías" },
            { n: "+20", l: "programas" },
            { n: "100%", l: "en línea" },
          ].map((s, i) => (
            <div key={i} className="card-clean py-3">
              <div className="font-heading text-xl font-bold text-[#0A2A66]">{s.n}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{s.l}</div>
            </div>
          ))}
        </section>

        {/* Categories */}
        <section className="px-4 mt-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base font-bold text-[#0A2A66]">Categorías</h3>
            <Link to="/catalogo" className="text-xs font-semibold text-[#1E5FD9]">Ver todo →</Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(CATEGORY_LABELS).map(([slug, label]) => {
              const IconCmp = LucideIcons[CATEGORY_ICONS[slug]] || GraduationCap;
              return (
                <Link
                  key={slug}
                  to={`/catalogo?cat=${slug}`}
                  data-testid={`category-${slug}`}
                  className="category-tile"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#EEF3FF] text-[#0A2A66] flex items-center justify-center">
                    <IconCmp size={20} />
                  </div>
                  <div className="font-heading text-sm font-bold text-[#0A2A66]">{label}</div>
                  <div className="text-[11px] text-slate-500">Descubrir →</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Featured */}
        <section className="px-4 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Star size={16} className="text-[#F5B914]" fill="#F5B914" />
            <h3 className="font-heading text-base font-bold text-[#0A2A66]">Programas destacados</h3>
          </div>
          <div className="space-y-3">
            {featured.slice(0, 5).map((p) => (
              <Link
                key={p.id}
                to={`/producto/${p.id}`}
                data-testid={`featured-${p.id}`}
                className="card-clean flex gap-3 p-3 items-center hover:border-[#1E5FD9] transition"
              >
                <img src={p.images?.[0] || CATEGORY_IMAGES[p.category]} alt="" className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <span className="badge-cat">{CATEGORY_LABELS[p.category]}</span>
                  <div className="font-heading font-bold text-sm text-[#0A2A66] mt-1 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 truncate">{p.short_description}</div>
                  <div className="text-[#F5B914] font-bold text-sm mt-1">{formatPrice(p.price_usd, currency)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="h-8" />
      </main>
    </>
  );
}
